import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../../components/Hero';
import CallToAction from '../../components/CallToAction';
import '../ProjectPages/ProjectPages.css';

// Import images
import aiHeroImage from '../../assets/images/ai.jpg';

const DataScienceEN = () => {
  const faqData = [
    {
      question: "What are data science and artificial intelligence solutions used for?",
      answer: "We analyze your business data to extract meaningful insights. We make future predictions, make automated decisions, and optimize processes. This way, businesses make better decisions, reduce costs, and increase competitiveness."
    },
    {
      question: "What sectors do you offer data science solutions for?",
      answer: "We offer data science and artificial intelligence solutions for many sectors such as education, health, finance, retail, and manufacturing. We develop customized solutions according to each sector's specific needs."
    },
    {
      question: "How does the data analysis process work?",
      answer: "We collect, clean, and analyze your business data. We prepare understandable reports and visual dashboards. We predict what might happen in the future and offer recommendations."
    },
    {
      question: "Are artificial intelligence solutions expensive?",
      answer: "Our solutions are customized according to your business needs. We offer different budget options. You can contact us for detailed information and special offers."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Data Science and AI Solutions",
    "provider": {
      "@type": "Organization",
      "name": "ProgenX"
    },
    "description": "Transform your business data into value with data science and artificial intelligence solutions.",
    "areaServed": "TR"
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="project-page data-science">
      <Helmet>
        <title>Data Science and Artificial Intelligence Solutions | Take Your Business to the Future - ProgenX 2025</title>
        <meta name="description" content="Transform your business data into meaningful insights with ProgenX data science and artificial intelligence solutions. Future predictions, automated decisions, process optimization. Turkey's leading data science company." />
        <meta name="keywords" content="data science, artificial intelligence solutions, machine learning, deep learning, big data analysis, data mining, business intelligence, predictive analytics, turkey data science, ai consulting" />
        <meta property="og:title" content="Data Science and Artificial Intelligence Solutions | ProgenX" />
        <meta property="og:description" content="Artificial intelligence and data science solutions that transform your business data into value. Better decisions, lower costs." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenxacademy.com/en/projects/data-science-ai" />
        <meta property="og:image" content="https://progenxacademy.com/ai.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://progenxacademy.com/en/projects/data-science-ai" />
        <link rel="alternate" hreflang="tr" href="https://progenxacademy.com/projeler/veri-bilimi-yapay-zeka" />
        <link rel="alternate" hreflang="en" href="https://progenxacademy.com/en/projects/data-science-ai" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <Hero 
        title="Data Science and Artificial Intelligence" 
        backgroundImage={aiHeroImage}
      />

      <div className="project-content">
        <div className="container">
          <section className="project-overview">
            <h2>Creating Value from Your Data</h2>
            <p>
              The data in your business's hands is actually a great treasure. However, if you don't process this treasure correctly, 
              you cannot create value. As ProgenX, with our artificial intelligence and data science solutions, 
              we extract meaningful insights from your data, make your decisions data-driven, and 
              increase your business's competitiveness.
            </p>
          </section>

          <section className="features">
            <h2>How Do We Add Value to Your Business?</h2>
            <div className="features-grid">
              <div className="feature-item">
                <h3>We Transform Your Data into Meaningful Information</h3>
                <p>
                  We collect and analyze all your business data and present it as understandable reports and 
                  visual dashboards. This way, you clearly see what you need to understand from your data.
                </p>
              </div>

              <div className="feature-item">
                <h3>We Make Future Predictions</h3>
                <p>
                  We predict what might happen in the future by analyzing your past data. 
                  Customer behaviors, sales trends, possible problems - we help you see everything in advance.
                </p>
              </div>

              <div className="feature-item">
                <h3>We Make Automated Decisions</h3>
                <p>
                  We enable you to make automated decisions by processing different data types such as images, 
                  sound, and text. We analyze customer comments, recognize images, and generate content.
                </p>
              </div>

              <div className="feature-item">
                <h3>We Optimize Your Processes</h3>
                <p>
                  We find where efficiency loss occurs by analyzing your business processes and 
                  ensure you use your resources in the best way. We detect risks in advance and reduce costs.
                </p>
              </div>
            </div>
          </section>

          <section className="benefits">
            <h2>What Do We Provide for Your Business?</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>Make Better Decisions</h3>
                <p>Let your data speak instead of your instincts. Increase your chances of success and reduce errors 
                by making data-driven decisions.</p>
              </div>

              <div className="benefit-item">
                <h3>Work More Efficiently</h3>
                <p>Do the same work with fewer resources by optimizing your processes. 
                Focus on your growth by saving time and energy.</p>
              </div>

              <div className="benefit-item">
                <h3>Get Ahead of Competitors</h3>
                <p>Stay one step ahead of your competitors with AI-powered solutions. 
                Understand your customers better, serve them better.</p>
              </div>

              <div className="benefit-item">
                <h3>Reduce Your Costs</h3>
                <p>Reduce unnecessary expenses with smart automation and optimization. 
                Increase your profitability by using your resources most efficiently.</p>
              </div>
            </div>
          </section>

          <section className="faq-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-container">
              {faqData.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">{faq.question}</h3>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <CallToAction 
        title="Start Creating Value from Your Data"
        subtitle="Contact us to analyze your business data and make your decisions data-driven."
        buttonText="Contact Us"
        buttonLink="/en/contact"
      />
    </div>
  );
};

export default DataScienceEN;
